import { LIVE_STREAMS_SUBSCRIPTION } from "@/graphql/subscriptions/streamTeamSubscriptions";
import { convertLetters } from "@/utils/letterConversion";
import { gql, useQuery, useSubscription } from "@apollo/client";
import { Twitch, User } from "lucide-react";
import { Link } from "react-router-dom";

const STREAM_TEAM_MEMBERS_QUERY = gql`
    query StreamTeamMembers {
        streamTeamMembers(activeOnly: true) {
            id
            player {
                id
                name
                avatarUrl
            }
            bio
            twitchUsername
            joinedAt
            displayOrder
            twitchAuth {
                profileImageUrl
            }
        }
        liveStreams {
            id
            streamTeamMember {
                id
                twitchUsername
            }
            isLive
        }
    }
`;

interface StreamTeamMember {
    id: string;
    player: {
        id: string;
        name: string;
        avatarUrl: string | null;
    };
    bio: string;
    twitchUsername: string;
    joinedAt: string;
    displayOrder?: number;
    twitchAuth?: {
        profileImageUrl: string;
    } | null;
}

interface LiveStream {
    id: string;
    streamTeamMember: {
        id: string;
        twitchUsername: string;
    };
    isLive: boolean;
}

export default function StreamTeamTeamPage() {
    const { loading, error, data, refetch } = useQuery<{
        streamTeamMembers: StreamTeamMember[];
        liveStreams: LiveStream[];
    }>(STREAM_TEAM_MEMBERS_QUERY, {
        fetchPolicy: "cache-and-network",
    });

    useSubscription(LIVE_STREAMS_SUBSCRIPTION, {
        onData: () => {
            refetch();
        },
    });

    if (loading) {
        return (
            <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12">
                <p className="font-garamond text-center text-zinc-400">
                    Loading stream team...
                </p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12">
                <p className="font-garamond text-center text-red-400">
                    Error loading stream team: {error.message}
                </p>
            </div>
        );
    }

    const members = data?.streamTeamMembers || [];
    const liveStreams = data?.liveStreams || [];

    // Sort members: live streamers first, then by display order
    const sortedMembers = [...members].sort((a, b) => {
        const aIsLive = liveStreams.some(
            (stream) =>
                stream.streamTeamMember.twitchUsername === a.twitchUsername &&
                stream.isLive,
        );
        const bIsLive = liveStreams.some(
            (stream) =>
                stream.streamTeamMember.twitchUsername === b.twitchUsername &&
                stream.isLive,
        );

        // Live streamers come first
        if (aIsLive && !bIsLive) return -1;
        if (!aIsLive && bIsLive) return 1;

        // Both live or both offline, sort by display order
        return (a.displayOrder || 0) - (b.displayOrder || 0);
    });

    return (
        <div className="mx-auto w-full px-4 py-8 sm:px-6 sm:py-12 lg:w-3/4">
            <div className="mb-6 sm:mb-8">
                <h1 className="font-prviking text-brand mb-3 text-center text-3xl sm:mb-4 sm:text-4xl lg:text-5xl">
                    {convertLetters("THE VANDERHEIM RAVENGUARD")}
                </h1>
                <h2 className="font-garamond text-center text-xl text-zinc-300 sm:text-2xl">
                    {convertLetters("Meet the valued Members of the VRX.")}
                </h2>
                <p className="font-garamond text-center text-base text-zinc-300 sm:text-lg lg:text-xl">
                    Explore each Member’s profile to view their schedule, recent
                    clips, VODs, and current live status — or watch them
                    directly on Twitch.
                </p>
            </div>

            <div className="mb-4">
                <p className="text-center text-zinc-400 italic">
                    Live Members are automatically filtered to the top. All
                    RAVENGUARD Members are otherwise ordered by Shield Value
                    (SV).
                </p>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3 xl:grid-cols-4">
                {sortedMembers.map((member) => {
                    const isLive = liveStreams.some(
                        (stream) =>
                            stream.streamTeamMember.twitchUsername ===
                                member.twitchUsername && stream.isLive,
                    );
                    return (
                        <StreamerCard
                            key={member.id}
                            member={member}
                            isLive={isLive}
                        />
                    );
                })}
            </div>
        </div>
    );
}

interface StreamerCardProps {
    member: StreamTeamMember;
    isLive: boolean;
}

function StreamerCard({ member, isLive }: StreamerCardProps) {
    // Prefer Twitch profile image over player avatar
    const avatarUrl =
        member.twitchAuth?.profileImageUrl || member.player.avatarUrl;

    return (
        <div className="group border-brand_stream_team flex flex-col rounded-xl border-2 bg-zinc-900 p-4 shadow-xl transition-all hover:border-yellow-500 sm:p-6">
            <div className="mb-3 flex items-center gap-3 sm:mb-4 sm:gap-4">
                <div className="relative shrink-0">
                    {avatarUrl ? (
                        <img
                            src={avatarUrl}
                            alt={member.player.name}
                            className="h-12 w-12 rounded-full border-2 border-yellow-500 object-cover sm:h-16 sm:w-16"
                        />
                    ) : (
                        <div className="bg-brand flex h-12 w-12 items-center justify-center rounded-full text-xl font-bold text-zinc-900 sm:h-16 sm:w-16 sm:text-2xl">
                            {member.player.name.charAt(0).toUpperCase()}
                        </div>
                    )}
                    {isLive && (
                        <div className="absolute -top-1 -right-1 flex items-center gap-1 rounded-full bg-red-600 px-1.5 py-0.5 text-xs font-bold text-white shadow-lg sm:px-2">
                            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white"></span>
                            LIVE
                        </div>
                    )}
                </div>
                <div className="min-w-0 flex-1">
                    <h3 className="font-prviking text-brand truncate text-xl sm:text-2xl">
                        {convertLetters(member.player.name)}
                    </h3>
                </div>
            </div>

            <div className="mb-3 flex-1 sm:mb-4">
                <p className="font-garamond text-sm text-zinc-300 sm:text-base">
                    {member.bio || "No bio available."}
                </p>
            </div>

            <div className="mt-auto flex gap-2">
                <Link
                    to={`/team/${member.twitchUsername}`}
                    className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-zinc-700 px-3 py-2 text-sm font-medium text-white transition-all hover:bg-zinc-600 sm:px-4 sm:text-base"
                >
                    <User className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span className="font-garamond">Profile</span>
                </Link>
                <a
                    href={`https://twitch.tv/${member.twitchUsername}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-purple-600 px-3 py-2 text-sm font-medium text-white transition-all hover:bg-purple-700 sm:px-4 sm:text-base"
                >
                    <Twitch className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span className="font-garamond">Watch</span>
                </a>
            </div>
        </div>
    );
}
